import type { Ref } from "vue";
import { useResizeObserver } from "@vueuse/core";
import { gsap, reducedMotion } from "~/utils/motion";

/**
 * Slides a highlight behind the item matching `selector` inside `container` (a
 * positioned element): segmented toggles, the sidebar, the playlist rail.
 * Bind `pill` to an absolutely positioned child at left/top 0. A change of
 * `trigger` slides it; a layout change snaps it. `ready` turns true once placed,
 * so the item can paint its own highlight until then (SSR, first paint).
 */
export function useSlidingHighlight(container: Ref<HTMLElement | null>, selector: string, trigger: () => unknown) {
  const pill = ref<HTMLElement | null>(null);
  const ready = ref(false);

  const place = (animate = false) => {
    const box = container.value;
    if (!box || !pill.value) return;
    const item = box.querySelector<HTMLElement>(selector);
    // Hidden item (none active, or inside a collapsed group): hide the pill too.
    if (!item?.offsetHeight) return void gsap.set(pill.value, { opacity: 0, overwrite: true });
    const to = { x: item.offsetLeft, y: item.offsetTop, width: item.offsetWidth, height: item.offsetHeight, opacity: 1 };
    if (animate && ready.value && !reducedMotion()) gsap.to(pill.value, { ...to, duration: 0.3, ease: "power3.out", overwrite: true });
    else gsap.set(pill.value, { ...to, overwrite: true });
    ready.value = true;
  };

  watch(trigger, () => nextTick(() => place(true)));
  watch([container, pill], () => nextTick(() => place()));
  useResizeObserver(container, () => place());
  onMounted(() => place());

  return { pill, ready, place };
}
